/**
 * The one focus-ring recipe, and the one 44px hit-area recipe.
 *
 * Both are defined here rather than repeated per component so "does this
 * control have a visible focus ring" and "is this control tappable" are single
 * facts about the kit instead of twelve independent judgment calls.
 */

/**
 * A visible ring on keyboard focus, invisible on mouse click.
 *
 * Uses `outline` rather than `ring`, deliberately: an outline follows the
 * element's own border box and needs no offset color, so the same class is
 * correct on a card, on a page background, and inside a dialog — three
 * surfaces where a ring-offset would have to be told which color to paint.
 */
export const FOCUS_RING =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring";

/**
 * Extends a control shorter than 44px to a 44px tap target without changing
 * its visual height, using an invisible centered overlay.
 *
 * The platform puts a hard 44x44 floor on every interactive element. A
 * compact 36px control (`--sm-control-height-sm`) is a real design need — a
 * filter chip row, an inline edit — so the answer is hit-slop, not a bigger
 * button. The parent must be `relative` and must not clip overflow.
 */
export const HIT_SLOP =
  "after:absolute after:inset-x-0 after:top-1/2 after:h-touch-target after:-translate-y-1/2 after:content-['']";

/**
 * Non-essential motion, disabled under `prefers-reduced-motion: reduce`.
 * Essential feedback (a press-down, a loading skeleton) keeps animating —
 * reduced motion is not the same as no feedback.
 */
export const REDUCED_MOTION_SAFE =
  "motion-reduce:transition-none motion-reduce:animate-none";

/**
 * A control that is RUNNING stays focusable. It carries `aria-disabled` and
 * returns early from its own handler, rather than carrying the `disabled`
 * attribute, because a disabled element cannot hold focus.
 *
 * A fan on a keyboard or a switch who pressed it has focus drop back to the
 * document, and tabs from the top of the page for the next attempt. The next
 * attempt is usually the whole point.
 *
 * `aria-disabled` is advice to assistive technology, not a block. The early
 * return in the handler is what actually refuses the press. On a
 * `type="submit"` button the handler must also `preventDefault` the click, or
 * the form still submits.
 *
 * This constant is the one class both busy treatments share. The rest differs
 * on purpose: `Button` mirrors all six of its own `disabled:` rules including
 * `pointer-events-none`, because every one of its variants carries a hover
 * background and its base carries `active:scale-[0.98]`, so a busy button must
 * not light up under the pointer. `InlineBanner`'s action carries neither, so
 * it uses `aria-disabled:cursor-not-allowed` and stays pointer-reachable.
 *
 * Neither choice affects focus: `pointer-events` is a hit-testing rule for
 * pointer devices and changes nothing about the tab order.
 */
export const BUSY_CONTROL = "aria-disabled:text-disabled-text";
